<template>
  <li class="header-notification-nav" ref="headerNotificationNav">
    <button class="header-action-btn header-notification-trigger" type="button" @click.stop="toggleNotificationsDropdown" title="Notificaciones">
      <i class="fa fa-bell"></i>
      <span v-if="unread > 0" class="notif-badge">{{ unread > 99 ? '99+' : unread }}</span>
    </button>

    <ul class="header-notification-dropdown" v-if="showNotificationsDropdown" ref="notificationsDropdown">
      <li class="notification-dropdown-header">
        <span class="notification-dropdown-title">Notificaciones</span>
        <div class="notification-header-actions">
          <button
            v-if="safeNotifications.length > 0"
            type="button"
            class="notification-header-btn is-read"
            aria-label="Marcar todas como leídas"
            data-notif-popover="true"
            data-bs-toggle="popover"
            data-bs-trigger="hover focus"
            data-bs-placement="bottom"
            data-bs-title="Marcar todas como leídas"
            data-bs-content="Marca todas las notificaciones actuales como leídas."
            @click.prevent="markAllNotificationsRead"
          >
            <i class="fa fa-envelope-open"></i>
          </button>
        </div>
      </li>
      <li v-if="notifLoading" class="notification-loading">
        <span>Cargando...</span>
      </li>
      <li v-else-if="safeNotifications.length === 0" class="notification-empty">
        <span>Sin notificaciones</span>
      </li>
      <li v-for="n in safeNotifications" :key="n.id" class="notification-item" :class="{ 'notif-unread': !n.is_read }">
        <a href="javascript:void(0)" class="notification-item-link" @click="openNotification(n)">
          <span class="notification-option-type">{{ notificationTitle(n) }}</span>
          <small class="notification-option-detail">{{ notificationBody(n) }}</small>
          <em class="notification-option-time">{{ notificationMeta(n) }}</em>
        </a>

      </li>
      <li v-if="page < totalPages" class="notification-load-more">
        <a href="#" @click.prevent="loadMoreNotifications">Ver más</a>
      </li>
    </ul>
  </li>

  <NotificationDetailModal
    :visible="detailModal.visible"
    :notification="detailModal.notification"
    :rfc="currentRfc"
    @close="closeDetailModal"
  />
</template>

<script>
import { Popover } from 'bootstrap';
import notificationsService from '@/services/notifications';
import NotificationDetailModal from '@/components/notifications/NotificationDetailModal.vue';

export default {
  name: 'Notifications',
  components: { NotificationDetailModal },
  data() {
    return {
      showNotificationsDropdown: false,
      notifications: [],
      unread: 0,
      notifLoading: false,
      page: 1,
      totalPages: 1,
      actionPopovers: [],
      detailModal: {
        visible: false,
        notification: null,
      },
    };
  },
  computed: {
    safeNotifications() {
      return Array.isArray(this.notifications) ? this.notifications : [];
    },
    currentRfc() {
      return localStorage.getItem('rfc') || '';
    },
  },
  methods: {
    extractPayload(response) {
      // Axios raw response includes status/config/headers; our interceptor usually returns payload directly.
      const isAxiosRaw = !!(response && typeof response === 'object' && 'status' in response && 'config' in response);
      return isAxiosRaw ? response.data : response;
    },
    emitHeaderDropdownOpen(source) {
      window.dispatchEvent(new CustomEvent('header-dropdown-open', { detail: { source } }));
    },
    initActionPopovers() {
      this.disposeActionPopovers();

      const nodes = this.$refs.headerNotificationNav?.querySelectorAll('[data-notif-popover="true"]') || [];
      this.actionPopovers = Array.from(nodes).map((node) => {
        try {
          return new Popover(node, {
            container: 'body',
            boundary: 'viewport',
          });
        } catch (_) {
          return null;
        }
      }).filter(Boolean);
    },
    disposeActionPopovers() {
      if (!Array.isArray(this.actionPopovers) || this.actionPopovers.length === 0) {
        return;
      }

      this.actionPopovers.forEach((instance) => {
        try {
          instance.dispose();
        } catch (_) {}
      });

      this.actionPopovers = [];
    },
    handleHeaderDropdownOpen(event) {
      const source = event && event.detail ? event.detail.source : '';
      if (source !== 'notifications') {
        this.showNotificationsDropdown = false;
        this.disposeActionPopovers();
      }
    },
    toggleNotificationsDropdown() {
      if (this.showNotificationsDropdown) {
        this.closeNotificationsDropdown();
        return;
      }

      this.showNotificationsDropdown = true;
      this.emitHeaderDropdownOpen('notifications');

      if (this.unread > 0) {
        this.fetchNotifications(1);
      } else {
        this.notifications = [];
        this.page = 1;
        this.totalPages = 1;
        this.$nextTick(() => this.initActionPopovers());
      }
    },
    closeNotificationsDropdown(event = null) {
      if (event) {
        const dropdown = this.$refs.notificationsDropdown;
        const trigger = this.$refs.headerNotificationNav?.querySelector('.header-notification-trigger') ?? null;
        const target = event.target;

        if ((dropdown && dropdown.contains(target)) || (trigger && trigger.contains(target))) {
          return;
        }
      }

      this.showNotificationsDropdown = false;
      this.disposeActionPopovers();
    },
    normalizedCurrentRfc() {
      return String(this.currentRfc || '').trim().toUpperCase();
    },
    refreshForCurrentRfc() {
      this.notifications = [];
      this.page = 1;
      this.totalPages = 1;
      this.fetchUnreadCount();

      if (this.showNotificationsDropdown) {
        this.fetchNotifications(1);
      }
    },
    handleRfcsStorageUpdated() {
      this.refreshForCurrentRfc();
    },
    handleStorageEvent(event) {
      if (!event || event.key === null || event.key === 'rfc' || event.key === 'rfcs') {
        this.refreshForCurrentRfc();
      }
    },
    async fetchUnreadCount() {
      try {
        const response = await notificationsService.getUnreadCount(this.normalizedCurrentRfc());
        const payload = this.extractPayload(response);
        this.unread = Number(payload?.unread ?? 0);
      } catch (_) {}
    },
    async fetchNotifications(page = 1) {
      this.notifLoading = true;
      try {
        const response = await notificationsService.getNotifications(page, true, this.normalizedCurrentRfc());
        const payload = this.extractPayload(response);

        // Accept both shapes:
        // 1) { data: [ ... ], page, total_pages, unread }
        // 2) { data: { data: [ ... ], current_page, last_page }, unread }
        let rows = [];
        if (Array.isArray(payload?.data)) {
          rows = payload.data;
        } else if (Array.isArray(payload?.data?.data)) {
          rows = payload.data.data;
        }

        this.notifications = page === 1 ? rows : [...this.safeNotifications, ...rows];
        this.unread = Number(payload?.unread ?? 0);
        this.page = Number(payload?.page ?? payload?.data?.current_page ?? 1);
        this.totalPages = Number(payload?.total_pages ?? payload?.data?.last_page ?? 1);
      } catch (_) {}
      this.notifLoading = false;

      if (this.showNotificationsDropdown) {
        this.$nextTick(() => this.initActionPopovers());
      }
    },
    async markReadNotification(notification) {
      if (!notification.is_read) {
        notification.is_read = true;
        try {
          const response = await notificationsService.markAsRead(notification.id, this.normalizedCurrentRfc());
          const payload = this.extractPayload(response);
          this.unread = Number(payload?.unread ?? 0);
        } catch (_) {}
      }
    },
    openNotification(notification) {
      this.markReadNotification(notification);
      this.disposeActionPopovers();
      this.showNotificationsDropdown = false;
      this.detailModal.notification = notification;
      this.detailModal.visible = true;
    },
    closeDetailModal() {
      this.detailModal.visible = false;
      this.detailModal.notification = null;
    },
    async markAllNotificationsRead() {
      if (this.unread <= 0) {
        return;
      }

      try {
        const response = await notificationsService.markAsRead(null, this.normalizedCurrentRfc());
        const payload = this.extractPayload(response);
        this.unread = Number(payload?.unread ?? 0);
        this.notifications.forEach(n => { n.is_read = true; });
      } catch (_) {}
    },
    loadMoreNotifications() {
      if (this.page < this.totalPages) {
        this.fetchNotifications(this.page + 1);
      }
    },
    formatPeriodLabel(period) {
      const raw = String(period || '').trim();
      const match = raw.match(/^(\d{4})-(\d{2})$/);
      if (!match) {
        return '';
      }

      const year = match[1];
      const month = match[2];
      const monthNames = {
        '01': 'Enero',
        '02': 'Febrero',
        '03': 'Marzo',
        '04': 'Abril',
        '05': 'Mayo',
        '06': 'Junio',
        '07': 'Julio',
        '08': 'Agosto',
        '09': 'Septiembre',
        '10': 'Octubre',
        '11': 'Noviembre',
        '12': 'Diciembre',
      };

      const monthName = monthNames[month] || '';
      return monthName ? `${monthName} ${year}` : '';
    },
    formatNotificationBody(body) {
      const text = String(body || '');
      return text.replace(/\b(\d{4})-(0[1-9]|1[0-2])\b/g, (_, year, month) => {
        const label = this.formatPeriodLabel(`${year}-${month}`);
        return label || `${year}-${month}`;
      });
    },
    isDownloadNotification(notification) {
      const type = String(notification?.type || '').trim();
      return type === 'download_received' || type === 'download_issued';
    },
    notificationTitle(notification) {
      if (!this.isDownloadNotification(notification)) {
        return String(notification?.title || '');
      }

      return notification.type === 'download_issued'
        ? 'CFDIs Emitidos'
        : 'CFDIs Recibidos';
    },
    notificationBody(notification) {
      if (!this.isDownloadNotification(notification)) {
        return this.formatNotificationBody(notification?.body);
      }

      const count = Number(notification?.data?.count ?? 0);
      const periodRaw = String(notification?.data?.period || '').trim();
      const periodLabel = this.formatPeriodLabel(periodRaw) || periodRaw;

      if (periodLabel) {
        return `Se detectaron ${count} CFDIs nuevos en el periodo ${periodLabel}.`;
      }

      return `Se detectaron ${count} CFDIs nuevos.`;
    },
    notificationMeta(notification) {
      return this.timeAgo(notification?.created_at);
    },
    timeAgo(dateStr) {
      if (!dateStr) return '';
      const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
      if (diff < 60) return 'hace un momento';
      if (diff < 3600) return `hace ${Math.floor(diff / 60)} min.`;
      if (diff < 86400) return `hace ${Math.floor(diff / 3600)} hr.`;
      return `hace ${Math.floor(diff / 86400)} dias`;
    },
  },
  mounted() {
    this.fetchUnreadCount();
    document.addEventListener('click', this.closeNotificationsDropdown);
    window.addEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.addEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.addEventListener('storage', this.handleStorageEvent);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeNotificationsDropdown);
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.removeEventListener('storage', this.handleStorageEvent);
    this.disposeActionPopovers();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeNotificationsDropdown);
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.removeEventListener('storage', this.handleStorageEvent);
    this.disposeActionPopovers();
  }
};
</script>

<style scoped>
.header-notification-nav {
  position: relative;
  display: flex;
  align-items: center;
}

.header-action-btn {
  border: 0;
  background: var(--white);
  height: 52px;
  min-height: 52px;
  min-width: 46px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid var(--sidebar-border);
  transition: all 0.15s ease;
  position: relative;
}

.header-action-btn i {
  font-size: 16px;
  color: var(--theme-font-color);
  opacity: 0.8;
}

.header-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px var(--sidebar-border);
}

.header-action-btn:hover i {
  color: var(--theme-deafult);
  opacity: 1;
}

.notif-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--theme-deafult);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  pointer-events: none;
}

.header-notification-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 400px;
  max-height: 460px;
  overflow-y: auto;
  margin: 0;
  padding: 0 0 8px 0;
  list-style: none;
  background-color: var(--white);
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  box-shadow: 0 2px 8px var(--sidebar-border);
  z-index: 20;
  transform-origin: top right;
  animation: headerDropdownIn 0.14s ease-out;
}

.header-notification-dropdown .notification-dropdown-header {
  padding: 10px 12px;
  border-bottom: 1px solid var(--sidebar-border);
  background: linear-gradient(100deg, rgba(247, 49, 100, 0.06) 0%, rgba(115, 102, 255, 0.08) 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-notification-dropdown .notification-dropdown-title {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--theme-deafult);
  letter-spacing: 0.02em;
}

.notification-header-actions {
  display: flex;
  gap: 8px;
}

.notification-header-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #f8f9fa;
}

.notification-header-btn i {
  font-size: 12px;
}

.notification-header-btn.is-read {
  color: #0f766e;
  border-color: rgba(15, 118, 110, 0.2);
  background: rgba(15, 118, 110, 0.08);
}

.notification-header-btn.is-read:hover {
  background: rgba(15, 118, 110, 0.16);
}

.notification-loading,
.notification-empty {
  padding: 20px 12px;
  text-align: center;
  font-size: 12px;
  color: var(--theme-font-color);
  opacity: 0.6;
}

.notification-item {
  position: relative;
}

.notification-item-link {
  display: block;
  padding: 9px 12px;
  text-decoration: none;
  color: var(--theme-font-color);
  position: relative;
}

.notification-item-link:hover {
  background-color: var(--sidebar-border);
}

.header-notification-dropdown li + li .notification-item-link {
  border-top: 1px solid var(--sidebar-border);
}

.notification-load-more {
  padding: 8px 12px;
  text-align: center;
  border-top: 1px solid var(--sidebar-border);
}

.notification-load-more a {
  font-size: 12px;
  font-weight: 700;
  color: var(--theme-deafult);
  text-decoration: none;
}

.notif-unread > a {
  background-color: rgba(115, 102, 255, 0.05);
}

.notif-unread > a::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--theme-deafult);
  border-radius: 0 2px 2px 0;
}

.notification-item-link .notification-option-type {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--theme-font-color);
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-item-link .notification-option-detail {
  display: block;
  font-size: 11px;
  color: var(--theme-font-color);
  opacity: 0.78;
  line-height: 1.2;
  margin-bottom: 4px;
}

.notification-item-link .notification-option-time {
  display: inline-block;
  font-style: normal;
  font-size: 10px;
  font-weight: 600;
  color: var(--theme-deafult);
  opacity: 0.9;
}

@keyframes headerDropdownIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 991.98px) {
  .header-notification-dropdown {
    position: fixed;
    top: 68px;
    left: 8px;
    right: 8px;
    width: auto;
    min-width: 0;
    max-height: min(72vh, 560px);
    z-index: 120;
    transform-origin: top center;
  }

  .header-notification-dropdown .notification-dropdown-title {
    font-size: 13px;
  }
}
</style>
